import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OrderedSet, List } from 'immutable';
import type { Moment } from 'moment';
import type { DateRange } from '@mui/x-date-pickers-pro';
import {
  OptionEventFactory,
  OptionUserFactory,
  ChipDateFactory,
  type Chip,
  type ChipProps,
} from '@/components/mui/Layouts/components/EventsHistoryPanel/components/FiltersBlock/helpers';
import useDidUpdateEffect from '@/hooks/useDidUpdateEffect';
import useDebounceValue from '@/hooks/useDebounceValue';
import { FormattedMessage } from 'react-intl';
import type { TrailFilterOptionPlain, TrailPanelFilters } from '@/domain/events/apFilters';

import EventsHistoryTextFilter from '@/components/mui/Layouts/components/EventsHistoryPanel/components/FiltersBlock/TextFilter';
import EventsHistoryFilterChips from '@/components/mui/Layouts/components/EventsHistoryPanel/components/FiltersBlock/FilterChips';
import EventsHistoryDropdownFilter from '@/components/mui/Layouts/components/EventsHistoryPanel/components/FiltersBlock/DropdownFilter';
import EventsHistoryDateRangeFilter from '@/components/mui/Layouts/components/EventsHistoryPanel/components/FiltersBlock/DateRangeFilter';
import ExportButton from '@/components/mui/Layouts/components/EventsHistoryPanel/components/ExportButton';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';

import SupervisedUserCircleOutlinedIcon from '@mui/icons-material/SupervisedUserCircleOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';

import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';

import elements from '@/components/elements';

interface EventsHistoryFiltersBlockProps {
  users: ReadonlyArray<TrailFilterOptionPlain>;
  types: ReadonlyArray<TrailFilterOptionPlain>;
  isBusy: boolean;
  onChangeFilters: (filters: TrailPanelFilters) => void;
  onChangeDate: (from: Date | null, to: Date | null) => void;
  onClickExport: () => void;
  dateFormat: string;
  isDisabledExport: boolean;
  initialSearchTypes?: ReadonlyArray<string>;
}

const Wrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${grey[300]}`,
}));

const toOptions = (
  options: ReadonlyArray<TrailFilterOptionPlain>,
  Factory: (props: Partial<ChipProps>) => Chip,
): List<Chip> => options.reduce((acc, option) => acc.push(Factory(option)), List<Chip>());

const valuesOf = (chips: OrderedSet<Chip>, type: Chip['type']): string[] =>
  chips
    .filter((chip) => chip.type === type)
    .toArray()
    .map((chip) => chip.value);

const EventsHistoryFiltersBlock: React.FC<EventsHistoryFiltersBlockProps> = ({
  users,
  types,
  isBusy,
  onChangeFilters,
  onChangeDate,
  onClickExport,
  dateFormat,
  isDisabledExport,
  initialSearchTypes = [],
}) => {
  const userOptions = useMemo(() => toOptions(users, OptionUserFactory), [users]);
  const typeOptions = useMemo(() => toOptions(types, OptionEventFactory), [types]);

  const [chips, setChips] = useState<OrderedSet<Chip>>(OrderedSet());
  const [text, setText] = useState('');
  const [dateRange, setDateRange] = useState<DateRange<Moment>>([null, null]);

  const debounceText = useDebounceValue(text, 500);

  const handleAdd = useCallback((chip: Chip) => {
    setChips((prevChips) => prevChips.add(chip));
  }, []);

  const emitFilters = useCallback(
    (next: OrderedSet<Chip>) => {
      onChangeFilters({
        users: valuesOf(next, 'user'),
        types: valuesOf(next, 'event'),
        texts: valuesOf(next, 'text'),
        freeText: debounceText,
      });
    },
    [onChangeFilters, debounceText],
  );

  const appliedPreset = useRef<ReadonlyArray<string> | null>(null);
  useEffect(() => {
    if (appliedPreset.current === initialSearchTypes) return;
    if (initialSearchTypes.length === 0 || typeOptions.size === 0) return;

    appliedPreset.current = initialSearchTypes;
    const next = chips.union(typeOptions.filter((option) => initialSearchTypes.includes(option.value)));
    // A preset that matches nothing, or that is already applied, leaves the set
    // reference-identical — the caller still has to hear that the preset is settled.
    if (next === chips) emitFilters(chips);
    else setChips(next);
  }, [typeOptions, initialSearchTypes, chips, emitFilters]);

  const handleRemove = useCallback(
    (chip: Chip) => {
      setChips((prevChips) => prevChips.delete(chip));

      if (chip.type === 'date') {
        setDateRange([null, null]);
        onChangeDate(null, null);
      }
    },
    [onChangeDate],
  );

  const handleChangeText = useCallback((value: string) => {
    setText(value);
  }, []);

  const handleChangeDate = useCallback(
    ([from, to]: [Moment, Moment]) => {
      const label = `${from.format(dateFormat)} - ${to.format(dateFormat)}`;
      const chipDate = ChipDateFactory({ label, value: label });

      setChips((prevChips) => {
        // find existed date chip, if true - update current date chip with new values
        if (prevChips.find((entry) => entry.type === 'date')) {
          return prevChips.map((chip) => (chip.type === 'date' ? chipDate : chip));
        }
        // else add to OrderedSet
        return prevChips.add(chipDate);
      });

      onChangeDate(from.toDate(), to.toDate());
      setDateRange([from, to]);
    },
    [onChangeDate, dateFormat],
  );

  useDidUpdateEffect(() => {
    emitFilters(chips);
  }, [chips, debounceText, onChangeFilters]);

  return (
    <Wrapper>
      <EventsHistoryTextFilter handleAdd={handleAdd} handleChangeText={handleChangeText} />

      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between" mb={1}>
        <EventsHistoryDateRangeFilter onChange={handleChangeDate} range={dateRange} />
        <EventsHistoryDropdownFilter
          options={userOptions}
          isBusy={isBusy}
          toggleText={<FormattedMessage id="eventsHistory.panel.filter.users" defaultMessage="Users" />}
          StartIcon={<SupervisedUserCircleOutlinedIcon />}
          handleOptionClick={handleAdd}
          dataElement={elements.history.dropdownFilter.usersDropdown}
        />
        <EventsHistoryDropdownFilter
          options={typeOptions}
          isBusy={isBusy}
          toggleText={<FormattedMessage id="eventsHistory.panel.filter.events" defaultMessage="Events" />}
          StartIcon={<ListAltOutlinedIcon />}
          handleOptionClick={handleAdd}
          dataElement={elements.history.dropdownFilter.eventsDropdown}
        />
        <Divider orientation="vertical" sx={{ height: 24 }} />
        <ExportButton onClick={onClickExport} isDisabled={isDisabledExport} />
      </Stack>

      <EventsHistoryFilterChips chips={chips} onRemove={handleRemove} />
    </Wrapper>
  );
};

export default EventsHistoryFiltersBlock;
